import type {
  BoardSnapshot,
  Client,
  CreateClientInput,
  Comment,
  CreateCommentInput,
  CreateSubtaskInput,
  CreateTaskInput,
  Subtask,
  Task,
  TaskActivity,
  UpdateClientInput,
  UpdateCommentInput,
  UpdateSubtaskInput,
  UpdateTaskInput,
  User,
  TeamEntry,
  InviteUserInput,
  UpdateTeamUserInput,
  Annotation,
  CreateAnnotationInput,
  BoardText,
  CreateBoardTextInput,
  UpdateBoardTextInput,
  BoardImage,
  CreateAttachmentInput,
  UpdateImageInput,
  RecycleItem,
  RecycleKind,
  Worklist,
  TaskWorklist,
  CreateWorklistInput,
  UpdateWorklistInput,
  CreateTaskWorklistInput,
} from "@board/shared";

export class ApiError extends Error {
  constructor(readonly status: number, readonly code: string) {
    super(`${status} ${code}`);
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const headers = new Headers(init?.headers);
  // Only declare a JSON body when there actually is one. Fastify rejects an
  // empty body sent with content-type: application/json, which 400s every
  // bodyless POST and DELETE — sign-out was the first casualty.
  if (init?.body !== undefined && !headers.has("content-type")) {
    headers.set("content-type", "application/json");
  }

  const response = await fetch(path, { credentials: "same-origin", ...init, headers });
  if (!response.ok) {
    const body = (await response.json().catch(() => ({}))) as { error?: string };
    throw new ApiError(response.status, body.error ?? "request_failed");
  }
  // 204 and other empty responses are valid; don't force them through JSON.
  if (response.status === 204 || response.headers.get("content-length") === "0") {
    return undefined as T;
  }
  return (await response.json()) as T;
}

const json = (body: unknown): RequestInit => ({ body: JSON.stringify(body) });

export const api = {
  me: () => request<{ user: User }>("/api/me"),
  signOut: () => request<{ ok: true }>("/auth/signout", { method: "POST" }),

  /** One consistent snapshot for first paint. */
  board: () => request<BoardSnapshot>("/api/board"),

  clients: {
    create: (input: CreateClientInput) =>
      request<{ client: Client }>("/api/clients", { method: "POST", ...json(input) }),
    update: (id: string, patch: UpdateClientInput) =>
      request<{ client: Client }>(`/api/clients/${id}`, { method: "PATCH", ...json(patch) }),
    remove: (id: string) => request<void>(`/api/clients/${id}`, { method: "DELETE" }),
    /** `data` is a data URL; the API checks the type and the size before storing it. */
    setLogo: (id: string, data: string) =>
      request<{ client: Client }>(`/api/clients/${id}/logo`, { method: "PUT", ...json({ data }) }),
    clearLogo: (id: string) =>
      request<{ client: Client }>(`/api/clients/${id}/logo`, { method: "DELETE" }),
    /** Who may see a private board. Admin-only in both directions. */
    access: (id: string) => request<{ user_ids: string[] }>(`/api/clients/${id}/access`),
    setAccess: (id: string, userIds: string[]) =>
      request<{ user_ids: string[] }>(`/api/clients/${id}/access`, {
        method: "PUT",
        ...json({ user_ids: userIds }),
      }),
  },

  tasks: {
    create: (input: CreateTaskInput) =>
      request<{ task: Task }>("/api/tasks", { method: "POST", ...json(input) }),
    update: (id: string, patch: UpdateTaskInput) =>
      request<{ task: Task }>(`/api/tasks/${id}`, { method: "PATCH", ...json(patch) }),
    remove: (id: string) => request<void>(`/api/tasks/${id}`, { method: "DELETE" }),
    activity: (id: string) =>
      request<{ activity: TaskActivity[] }>(`/api/tasks/${id}/activity`),
  },

  worklists: {
    create: (input: CreateWorklistInput) =>
      request<{ worklist: Worklist }>("/api/worklists", { method: "POST", ...json(input) }),
    update: (id: string, patch: UpdateWorklistInput) =>
      request<{ worklist: Worklist }>(`/api/worklists/${id}`, { method: "PATCH", ...json(patch) }),
    setLogo: (id: string, data: string) =>
      request<{ worklist: Worklist }>(`/api/worklists/${id}/logo`, { method: "PUT", ...json({ data }) }),
    clearLogo: (id: string) =>
      request<{ worklist: Worklist }>(`/api/worklists/${id}/logo`, { method: "DELETE" }),
    link: (input: CreateTaskWorklistInput) =>
      request<{ task_worklist: TaskWorklist; task: Task }>("/api/task-worklists", {
        method: "POST",
        ...json(input),
      }),
    unlink: (taskId: string, worklistId: string) =>
      request<void>(`/api/tasks/${taskId}/worklists/${worklistId}`, { method: "DELETE" }),
  },

  subtasks: {
    create: (input: CreateSubtaskInput) =>
      request<{ subtask: Subtask }>("/api/subtasks", { method: "POST", ...json(input) }),
    update: (id: string, patch: UpdateSubtaskInput) =>
      request<{ subtask: Subtask }>(`/api/subtasks/${id}`, { method: "PATCH", ...json(patch) }),
    remove: (id: string) => request<void>(`/api/subtasks/${id}`, { method: "DELETE" }),
  },

  comments: {
    create: (input: CreateCommentInput) =>
      request<{ comment: Comment }>("/api/comments", { method: "POST", ...json(input) }),
    update: (id: string, patch: UpdateCommentInput) =>
      request<{ comment: Comment }>(`/api/comments/${id}`, { method: "PATCH", ...json(patch) }),
    remove: (id: string) => request<void>(`/api/comments/${id}`, { method: "DELETE" }),
  },

  team: {
    list: () => request<{ people: TeamEntry[] }>("/api/admin/team"),
    invite: (input: InviteUserInput) =>
      request<{ people: TeamEntry[] }>("/api/admin/invitations", {
        method: "POST",
        ...json(input),
      }),
    revoke: (email: string) =>
      request<void>(`/api/admin/invitations/${encodeURIComponent(email)}`, { method: "DELETE" }),
    update: (id: string, patch: UpdateTeamUserInput) =>
      request<{ people: TeamEntry[] }>(`/api/admin/users/${id}`, {
        method: "PATCH",
        ...json(patch),
      }),
  },

  annotations: {
    create: (input: CreateAnnotationInput) =>
      request<{ annotation: Annotation }>("/api/annotations", { method: "POST", ...json(input) }),
    remove: (id: string) => request<void>(`/api/annotations/${id}`, { method: "DELETE" }),
  },

  boardText: {
    create: (input: CreateBoardTextInput) =>
      request<{ text_item: BoardText }>("/api/board-text", { method: "POST", ...json(input) }),
    update: (id: string, patch: UpdateBoardTextInput) =>
      request<{ text_item: BoardText }>(`/api/board-text/${id}`, { method: "PATCH", ...json(patch) }),
    remove: (id: string) => request<void>(`/api/board-text/${id}`, { method: "DELETE" }),
  },

  attachments: {
    create: (input: CreateAttachmentInput) =>
      request<{ attachment: BoardImage }>("/api/attachments", { method: "POST", ...json(input) }),
    update: (id: string, patch: UpdateImageInput) =>
      request<{ attachment: BoardImage }>(`/api/attachments/${id}`, { method: "PATCH", ...json(patch) }),
    remove: (id: string) => request<void>(`/api/attachments/${id}`, { method: "DELETE" }),
  },

  recycleBin: {
    list: () => request<{ items: RecycleItem[] }>("/api/recycle-bin"),
    restore: (kind: RecycleKind, id: string) =>
      request<{ restored: true }>(`/api/recycle-bin/${kind}/${id}/restore`, { method: "POST" }),
    purge: (kind: RecycleKind, id: string) =>
      request<void>(`/api/recycle-bin/${kind}/${id}`, { method: "DELETE" }),
  },
};
